import { Trans } from '@lingui/react/macro';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { signOut, useSession } from '../lib/auth-client.js';
import { FeedbackModal } from './feedback-modal.js';

// User menu — avatar trigger that opens a dropdown with the founder's
// identity at the top, an "activity" shortcut, and sign-out. Shared
// between the full TopNav and the minimal BrandsNav so both surfaces
// expose the same user controls in the same shape.

function firstLetterOf(name: string | null | undefined, email: string | null | undefined): string {
  const source = (name?.trim() || email?.trim() || '').replace(/^[^a-z0-9]+/i, '');
  return source.charAt(0).toUpperCase() || '✦';
}

export function UserMenu() {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const user = session?.user;
  const avatarUrl = user?.image ?? null;
  const fallback = firstLetterOf(user?.name, user?.email);

  const [open, setOpen] = useState(false);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Click-outside + escape to close. Mousedown so the close fires
  // before a click inside the menu lands on a child handler.
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate({ to: '/login' });
  };

  // First word of the name so the trigger stays compact in the nav
  // bar; full name still shows in the dropdown header on open. Falls
  // back to the email's local-part when no name is set, then to "you".
  const displayLabel =
    user?.name?.trim().split(/\s+/)[0] ?? user?.email?.trim().split('@')[0] ?? 'you';

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user?.name ? `${user.name} — menu` : 'user menu'}
        className={`flex items-center gap-2 rounded-full border border-[var(--line)] bg-white py-1 pl-1 pr-2.5 text-[var(--ink)] transition-colors hover:border-[var(--ink)]/40 ${
          open ? 'border-[var(--ink)]/60' : ''
        }`}
      >
        <span className="grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-full bg-[var(--plum)] text-[var(--cream)]">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              referrerPolicy="no-referrer"
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="font-mono text-[10.5px] font-bold uppercase tracking-[0.02em]">
              {fallback}
            </span>
          )}
        </span>
        {/* Display label hides on mobile — the avatar + dropdown chevron
            are enough to convey "this is you, tap for menu". Frees the
            ~110px the label occupies for the bell + hamburger that
            have nowhere else to live on a narrow viewport. */}
        <span className="hidden max-w-[120px] truncate text-[12.5px] font-semibold lowercase sm:inline">
          {displayLabel}
        </span>
        {/* Chevron rotates 180° when the dropdown is open so the
            founder gets a clear "this is interactive" affordance. */}
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={`shrink-0 text-[var(--ink)]/55 transition-transform duration-150 ${
            open ? 'rotate-180' : ''
          }`}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-lg border border-[var(--ink)] bg-white shadow-[4px_4px_0_var(--plum)]"
        >
          <div className="border-b border-[var(--line)] px-3 py-3">
            <p className="text-[13px] font-bold leading-tight text-[var(--ink)]">
              {user?.name ?? user?.email ?? 'founder'}
            </p>
            {user?.email ? (
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                {user.email}
              </p>
            ) : null}
          </div>
          {/* User menu is intentionally minimal now — workspace-level
              navigation lives in the workspace badge dropdown next to
              the wordmark. Only personal surfaces (your profile, sign
              out) belong here. */}
          <ul className="flex flex-col py-1">
            <li>
              <Link
                to="/profile"
                onClick={() => setOpen(false)}
                role="menuitem"
                className="block px-3 py-2 text-[12.5px] text-[var(--ink)]/85 no-underline hover:bg-[var(--paper)]"
              >
                {'↘'} <Trans>profile</Trans>
              </Link>
            </li>
            {/* Personal settings, so they sit with profile rather than in
                the workspace badge's dropdown (brands / team / activity /
                billing). Quiet hours and product-update mail are the same
                whichever brand you're looking at — that scope split is the
                whole point of spec 1151ae36. */}
            <li>
              <Link
                to="/settings/notifications"
                onClick={() => setOpen(false)}
                role="menuitem"
                className="block px-3 py-2 text-[12.5px] text-[var(--ink)]/85 no-underline hover:bg-[var(--paper)]"
              >
                {'↘'} <Trans>notifications</Trans>
              </Link>
            </li>
            {/* Feedback belongs here for the same reason profile does: it is
                a PERSONAL surface, not workspace navigation. It closes the
                menu and opens the modal rather than routing, because a
                founder reporting a bug should not lose the page the bug is
                on — the modal collects that href on submit. */}
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setFeedbackOpen(true);
                }}
                role="menuitem"
                className="block w-full px-3 py-2 text-left text-[12.5px] text-[var(--ink)]/85 hover:bg-[var(--paper)]"
              >
                {'↘'} <Trans>send feedback</Trans>
              </button>
            </li>
          </ul>
          <div className="border-t border-[var(--line)]">
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
              role="menuitem"
              className="block w-full px-3 py-2 text-left text-[12.5px] text-[var(--pulse)] hover:bg-[var(--paper)] disabled:opacity-60"
            >
              {signingOut ? <Trans>signing out…</Trans> : <Trans>↘ sign out</Trans>}
            </button>
          </div>
        </div>
      ) : null}
      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}
